import { effect, inject, Injectable, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { isNotUndefined, WINDOW } from '@kaseya-utils';
import { PermissionsFacade } from '@core/permissions/facades/permissions.facade';
import { Permission } from '@core/permissions/models/permission.enum';
import { AuthorizationService } from '@core/authorization/services/authorization.service';
import { AdaWindow } from '@core/ada/models/ada-window.interface';
import { adaSettings } from '@core/ada/models/ada-settings.interface';
import { AdaTokenFacade } from '@core/ada/facades/ada-token.facade';
import { distinctUntilChanged, filter, map } from 'rxjs';
import { environment } from '@env/environment';
import { DOCUMENT } from '@angular/common';
import { AdaFacade } from '@core/ada/facades/ada.facade';
import { AdaEventsFacade } from '@core/ada/facades/ada-events.facade';
import { RouteDataService } from '@core/route-data/services/route-data.service';

@Injectable({ providedIn: 'root' })
export class AdaIntegrationFacade {
  private readonly adaScriptUrl = environment.adaScriptUrl;
  private readonly authorizationService = inject(AuthorizationService);
  private readonly adaTokenFacade = inject(AdaTokenFacade);
  private readonly adaFacade = inject(AdaFacade);
  private readonly adaEventsFacade = inject(AdaEventsFacade);
  private readonly window = inject<AdaWindow>(WINDOW);
  private readonly permissionsFacade = inject(PermissionsFacade);
  private readonly document = inject(DOCUMENT);
  private readonly routeDataService = inject(RouteDataService);
  private readonly hasAdaModify = this.permissionsFacade.hasPermission(Permission.AdaModify);

  private adaScriptElement?: HTMLScriptElement;
  private isAdaEmbedStopped = false;

  readonly isChatHidden = toSignal(
    this.routeDataService.getDataWithTheDeepestProperty('hideAdaChat').pipe(
      map(data => !!data?.hideAdaChat),
      distinctUntilChanged()
    ),
    { initialValue: false }
  );

  constructor() {
    this.initializeAdaOnLogin();
    this.clearAdaOnLogout();
    this.watchTokenAndOpenChat();
    this.watchChatVisibility();
  }

  openAdaChat(): void {
    if (!this.window.adaEmbed) {
      return;
    }

    if (this.adaFacade.isChatInitialized()) {
      this.toggleChat();
      return;
    }

    this.adaTokenFacade.loadToken();
  }

  private initializeAdaOnLogin(): void {
    this.authorizationService.onLoginCompleted.pipe(filter(() => this.hasAdaModify() && !this.adaScriptElement)).subscribe(() => {
      this.setAdaSettings();

      if (this.adaScriptUrl) {
        this.loadAdaScript();
      }
    });
  }

  private loadAdaScript(): void {
    this.adaScriptElement = this.document.createElement('script');
    this.adaScriptElement.src = this.adaScriptUrl;
    this.adaScriptElement.async = true;
    this.adaScriptElement.onload = (): void => this.startAdaEmbedIfStopped();
    this.adaScriptElement.onerror = (): void => this.removeAda();
    this.document.head.prepend(this.adaScriptElement);
  }

  private startAdaEmbedIfStopped(): void {
    if (!this.isAdaEmbedStopped) {
      return;
    }

    this.window.adaEmbed?.start(this.window.adaSettings ?? adaSettings);
    this.isAdaEmbedStopped = false;
  }

  private watchTokenAndOpenChat(): void {
    effect(() => {
      const token = this.adaTokenFacade.adaToken();

      if (token) {
        untracked(() => this.applyToken(token));
      }
    });
  }

  private applyToken(token: string): void {
    if (this.isChatHidden()) {
      this.adaTokenFacade.clearToken();
      return;
    }

    this.window.adaEmbed?.setSensitiveMetaFields({ accessToken: token });

    if (this.adaFacade.isChatInitialized()) {
      return;
    }

    this.toggleChat();
    this.adaFacade.setChatAsInitialized();
  }

  private watchChatVisibility(): void {
    effect(() => {
      const isChatHidden = this.isChatHidden();

      untracked(() => (isChatHidden ? this.collapseChat() : this.refreshTokenIfChatInitialized()));
    });
  }

  private collapseChat(): void {
    if (this.adaFacade.isChatOpened()) {
      this.toggleChat();
    }
  }

  private refreshTokenIfChatInitialized(): void {
    if (!this.adaFacade.isChatInitialized()) {
      return;
    }

    this.adaTokenFacade.clearToken();
    this.adaTokenFacade.loadToken();
  }

  private toggleChat(): void {
    this.window.adaEmbed?.toggle();
  }

  private setAdaSettings(): void {
    this.window.adaSettings = {
      ...adaSettings,
      toggleCallback: (isOpen: boolean): void => this.adaFacade.updateChatToggleState(isOpen),
      onAdaEmbedLoaded: (): void => {
        this.adaFacade.setAdaEmbedAsLoaded();
        this.adaEventsFacade.subscribeToAdaEvents();
      },
      conversationEndCallback: (): void => this.resetChatOnConversationEnd(),
    };
  }

  private resetChatOnConversationEnd(): void {
    this.window.adaEmbed?.reset?.();
    this.adaTokenFacade.clearToken();
    this.adaFacade.resetConversation();
  }

  private clearAdaOnLogout(): void {
    this.authorizationService.onLogout.pipe(filter(() => !!this.adaScriptElement)).subscribe(() => this.removeAda());
  }

  private removeAda(): void {
    this.clearAdaData();
    this.removeAdaScript();
  }

  private removeAdaScript(): void {
    if (!isNotUndefined(this.adaScriptElement)) {
      return;
    }

    this.adaScriptElement.onload = null;
    this.adaScriptElement.onerror = null;
    this.adaScriptElement.remove();
    this.adaScriptElement = undefined;
  }

  private clearAdaData(): void {
    this.adaEventsFacade.unsubscribeFromAdaEvents();
    this.adaTokenFacade.clearToken();
    this.window.adaEmbed?.deleteHistory();
    this.window.adaEmbed?.stop();
    this.window.adaSettings = undefined;
    this.adaFacade.clearState();
    this.isAdaEmbedStopped = true;
  }
}
